import { useQuery } from '@apollo/client';
import { Filter, Table, Typography } from '@otp/ui';
import { MembersAssigned } from '@otp/icons';
import {
  Actions,
  AppArea,
  SortingDirection,
  StoreModel,
  Subjects,
  UsersDocument,
  UsersQuery,
} from 'src/graphql/generated/graphql';
import { getUnassignedOTPsTableConfig } from './otps-unassigned-list-config';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from 'src/utils/can';

type OTPsUnassignedListProps = {
  store: Partial<StoreModel>;
};

const PAGINATION_LIMIT = 10;

export const OTPsUnassignedList = ({ store }: OTPsUnassignedListProps) => {
  const { t } = useTranslation();
  const { can } = useCan();
  const [search, setSearch] = useState('');
  const canAccess = can({
    action: Actions.Assign,
    subject: Subjects.Stores,
    appArea: AppArea.Common,
  });

  const { loading, data } = useQuery<UsersQuery>(UsersDocument, {
    variables: {
      filter: {
        search: search,
        unassignedToStoreId: store.id,
      },
      sorting: {
        lastName: SortingDirection.Asc,
      },
      pagination: {
        limit: PAGINATION_LIMIT,
        offset: 0,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    skip: !canAccess,
  });

  if (!canAccess) {
    return null;
  }

  const users = data?.users?.records || [];
  const totalCount = data?.users?.pagination?.totalCount || 0;

  const tableConfig = getUnassignedOTPsTableConfig({ store, users }, { t });
  const title = `${t(
    'manage-restaurants.otps.unassigned',
    'Unassigned',
  )} (${totalCount})`;

  const onFilterChange = (filter: { search?: string }) => {
    setSearch(filter.search || '');
  };

  return (
    <>
      <Typography className="flex gap-2" variant="subtitle1" fontWeight="bold">
        <MembersAssigned fill="red" />
        {title}
      </Typography>
      <Filter
        className="!bg-white !p-0 !pt-2"
        searchInputProps={{
          label: t('manage-restaurants.otps.search', 'Search'),
        }}
        onSubmit={onFilterChange}
      />
      <Table
        title=" "
        options={{
          pagination: false,
          sort: false,
          count: totalCount,
        }}
        loading={loading}
        data={users}
        columns={tableConfig}
      />
    </>
  );
};
